import type { Thrown } from '@lib/helpers/thrown'

/**
 * Thin logging wrapper around `console`. Components and services must log
 * through this module rather than calling `console` directly — the `no-console`
 * lint rule is enforced everywhere except here (see eslint.config.js), so this
 * file is the single sanctioned console boundary. Swapping in a real logging /
 * error-reporting backend later means changing only this file.
 */

/** A JSON-safe value acceptable in a structured log context. */
type LogValue = string | number | boolean | null

/** Structured context attached to a log line (keys → primitive values). */
export interface LogContext {
  [key: string]: LogValue | undefined
}

function timestamp(): string {
  return new Date().toISOString()
}

const logger = {
  info(message: string, context?: LogContext): void {
    console.info(`[INFO] ${timestamp()}: ${message}`, context ?? '')
  },
  warn(message: string, context?: LogContext): void {
    console.warn(`[WARN] ${timestamp()}: ${message}`, context ?? '')
  },
  error(message: string, context?: LogContext): void {
    console.error(`[ERROR] ${timestamp()}: ${message}`, context ?? '')
  },
  /**
   * A failed call. A 4xx answer from the API is the product working as
   * designed (a blocked status change, a validation message), so it's logged
   * at info; network errors and 5xx stay errors.
   */
  failure(message: string, error: Thrown, context?: LogContext): void {
    const status = httpStatus(error)
    const full: LogContext = {
      ...context,
      error: toErrorMessage(error),
      ...(status != null ? { status } : {}),
    }
    if (status != null && status >= 400 && status < 500) {
      console.info(`[INFO] ${timestamp()}: ${message}`, full)
    } else {
      console.error(`[ERROR] ${timestamp()}: ${message}`, full)
    }
  },
}

/** The HTTP status on an API error, without importing the client (it imports this file). */
function httpStatus(error: Thrown): number | null {
  if (typeof error !== 'object' || error === null || !('status' in error)) return null
  return typeof error.status === 'number' ? error.status : null
}

/** Normalize anything thrown (or a query's error) to a human-readable message. */
export function toErrorMessage(error: Thrown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'An unexpected error occurred'
}

export default logger
